import React, { useEffect, useRef, useState } from "react";
import { useMixer } from "@/context/MixerContext";
import { Eye, EyeOff } from "lucide-react";

/**
 * Full-screen music-reactive visualizer sitting behind the mixer. Reads from
 * the shared MixerEngine's masterAnalyser. Toggleable, non-blocking.
 */
export default function Visualizer() {
  const { engine, ready } = useMixer();
  const canvasRef = useRef(null);
  const [on, setOn] = useState(true);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!ready || !engine || !on) {
      const c = canvasRef.current;
      if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
      cancelAnimationFrame(rafRef.current);
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const analyser = engine.masterAnalyser;
    const bins = analyser.frequencyBinCount;
    const buf = new Uint8Array(bins);
    let t0 = performance.now();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const render = () => {
      const w = canvas.width, h = canvas.height;
      const now = performance.now();
      const t = (now - t0) / 1000;
      analyser.getByteFrequencyData(buf);
      // subtle fade for trails
      ctx.fillStyle = "rgba(11,14,20,0.30)";
      ctx.fillRect(0, 0, w, h);

      // Center pulsing rings driven by bass energy
      let bass = 0; for (let i = 0; i < 8; i++) bass += buf[i]; bass /= 8 * 255;
      let mid = 0; for (let i = 40; i < 90; i++) mid += buf[i]; mid /= 50 * 255;
      let treble = 0; for (let i = 100; i < bins; i++) treble += buf[i]; treble /= (bins - 100) * 255;

      const cx = w / 2, cy = h / 2;
      const maxR = Math.min(w, h) * 0.42;

      // Radial spectrum bars (frequency-colored)
      const bars = 96;
      for (let i = 0; i < bars; i++) {
        const idx = Math.floor((i / bars) * bins * 0.7);
        const v = buf[idx] / 255;
        const angle = (i / bars) * Math.PI * 2 + t * 0.2;
        const r0 = maxR * (0.35 + bass * 0.15);
        const r1 = r0 + v * maxR * 0.55;
        const hue = i < bars * 0.33 ? "#00F0FF" : i < bars * 0.66 ? "#FF8C00" : "#FF2A6D";
        ctx.strokeStyle = hue;
        ctx.globalAlpha = 0.55 + v * 0.45;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(angle) * r0, cy + Math.sin(angle) * r0);
        ctx.lineTo(cx + Math.cos(angle) * r1, cy + Math.sin(angle) * r1);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // Concentric rings, pulsing on bass
      for (let ring = 0; ring < 3; ring++) {
        ctx.strokeStyle = ["#00F0FF", "#FF8C00", "#FF2A6D"][ring];
        ctx.globalAlpha = 0.25 + bass * 0.5;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, maxR * (0.35 + bass * 0.15) - ring * 8, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      // Corner strobes on treble hits
      if (treble > 0.6) {
        ctx.fillStyle = `rgba(255,255,255,${(treble - 0.6) * 0.4})`;
        ctx.fillRect(0, 0, w, h);
      }

      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [ready, engine, on]);

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="visualizer-canvas"
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 0, opacity: on ? 0.55 : 0 }}
      />
      <button
        data-testid="visualizer-toggle"
        onClick={() => setOn(v => !v)}
        className="fixed bottom-4 right-4 z-50 w-10 h-10 rounded-full bg-[#121620]/90 backdrop-blur border border-[#2A354B] text-[#94A3B8] hover:text-white hover:border-[#00F0FF] flex items-center justify-center transition-colors"
        title={on ? "Hide visualizer" : "Show visualizer"}
      >
        {on ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
      </button>
    </>
  );
}
